/**
 * @voltxampere/web — Realistic mode store (Sprint 59)
 *
 * Master toggle for the Aşama 2 foundation. When `enabled` is true
 * the thermal compute pipeline (Sprint 59 — `packages/engine/src/
 * thermal/`) runs after every DC OP and the ERC overheating rule
 * starts firing. When off, the engine follows the idealize path
 * (no thermal compute, no overheating warnings, mevcut davranış
 * birebir korunur).
 *
 * Full Aşama 2 surface (LED explosion, capacitor swelling, heat-
 * map overlay, particle pool, 8 Web Audio effects) ships post-
 * launch Sprint 65+ per ADR-0093 — Tesla pattern: infrastructure
 * ready, OTA software later.
 *
 * Persisted via IDB SettingsRecord.realistic; boot hydrate restores
 * the user's choice across sessions. Pattern mirrors Sprint 53
 * playback-speed.ts.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

/** Default ambient temperature (°C) — IEC reference. */
export const DEFAULT_AMBIENT_TEMP_C = 25;

/** Master "realistic mode" toggle. Default off. */
export const realisticModeEnabled = signal<boolean>(false);

/** Ambient temperature in °C used by the steady-state estimator. */
export const ambientTempC = signal<number>(DEFAULT_AMBIENT_TEMP_C);

let initialised = false;

/**
 * Hydrate the signals from IDB SettingsRecord. Idempotent — call
 * once at boot. Failures (private mode IDB, schema migration) fall
 * back to the in-memory defaults.
 */
export async function initRealisticMode(): Promise<void> {
  if (initialised) return;
  initialised = true;
  try {
    const settings = await getSettings();
    if (settings === null) return;
    if (settings.realistic?.enabled === true) {
      realisticModeEnabled.value = true;
    }
    const amb = settings.realistic?.ambientTempC;
    if (typeof amb === "number" && Number.isFinite(amb)) {
      ambientTempC.value = amb;
    }
  } catch {
    // Idempotent: leave defaults. The mode is off-by-default so
    // a hydrate failure is silent + safe.
  }
}

/**
 * Set the realistic mode toggle + persist. Returns the new value so
 * UI components can chain without re-reading the signal.
 */
export async function setRealisticMode(next: boolean): Promise<boolean> {
  realisticModeEnabled.value = next;
  try {
    await mergeSettings({ realistic: { enabled: next, ambientTempC: ambientTempC.value } });
  } catch {
    // Persistence failure does not roll the signal back — the user
    // sees their click take effect; the next boot may revert. Same
    // pattern as the existing playback-speed / thermal stores.
  }
  return next;
}

/** Test helper — full cold reset. */
export function resetRealisticModeForTests(): void {
  initialised = false;
  realisticModeEnabled.value = false;
  ambientTempC.value = DEFAULT_AMBIENT_TEMP_C;
}
